"use client";

import React, { useState, useRef, useEffect } from "react";
import styles from "./STPopover.module.css";
import clsx from "clsx";

export const STPopover = ({
  children,
  content,
  placement = "bottom-right",
  className,
  contentClassName,
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? controlledSetIsOpen : setInternalIsOpen;

  const popoverRef = useRef(null);
  const contentRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

  useIsomorphicLayoutEffect(() => {
    if (isOpen && contentRef.current && popoverRef.current) {
      const updatePosition = () => {
        if (!contentRef.current || !popoverRef.current) return;
        const wrapperRect = popoverRef.current.getBoundingClientRect();
        const width = contentRef.current.offsetWidth;
        const height = contentRef.current.offsetHeight;
        const padding = 16;

        let baseLeft = 0;
        let baseRight = 0;
        let baseTop = 0;
        let baseBottom = 0;

        if (placement === "bottom-right") {
          baseRight = wrapperRect.right;
          baseLeft = wrapperRect.right - width;
          baseTop = wrapperRect.bottom + 12;
          baseBottom = baseTop + height;
        } else if (placement === "bottom-left") {
          baseLeft = wrapperRect.left;
          baseRight = wrapperRect.left + width;
          baseTop = wrapperRect.bottom + 12;
          baseBottom = baseTop + height;
        } else if (placement === "top-right") {
          baseRight = wrapperRect.right;
          baseLeft = wrapperRect.right - width;
          baseBottom = wrapperRect.top - 12;
          baseTop = baseBottom - height;
        } else if (placement === "top-left") {
          baseLeft = wrapperRect.left;
          baseRight = wrapperRect.left + width;
          baseBottom = wrapperRect.top - 12;
          baseTop = baseBottom - height;
        }

        let newX = 0;
        let newY = 0;

        if (baseLeft < padding) {
          newX = padding - baseLeft;
        } else if (baseRight > window.innerWidth - padding) {
          newX = window.innerWidth - padding - baseRight;
        }

        if (baseTop < padding) {
          newY = padding - baseTop;
        } else if (baseBottom > window.innerHeight - padding) {
          newY = window.innerHeight - padding - baseBottom;
        }

        if (
          Math.abs(newX - offsetRef.current.x) > 1 ||
          Math.abs(newY - offsetRef.current.y) > 1
        ) {
          offsetRef.current = { x: newX, y: newY };
          setOffset({ x: newX, y: newY });
        }
      };

      updatePosition();

      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }

    offsetRef.current = { x: 0, y: 0 };
    setOffset({ x: 0, y: 0 });
  }, [isOpen, placement]);

  return (
    <div className={clsx(styles.popoverWrapper, className)} ref={popoverRef}>
      <div onClick={() => setIsOpen(!isOpen)} className={styles.triggerWrapper}>
        {children}
      </div>
      {isOpen && (
        <div
          ref={contentRef}
          className={clsx(
            styles.content,
            styles[placement],
            contentClassName,
            "position-absolute bg-clr-white radius-sm space-xs-p",
          )}
          style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
        >
          {typeof content === "function"
            ? content({ close: () => setIsOpen(false) })
            : content}
        </div>
      )}
    </div>
  );
};
